import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { AddPhotoControl } from './AddPhotoControl';
import {
  getJournalEntry,
  unlinkPhotoFromEntry,
  type AttachedImage,
} from '../../services/journalService';
import { useTranslation } from '../../i18n/useTranslation';
import './JournalEntryPhotos.css';

export interface JournalEntryPhotosProps {
  aquariumId: string;
  entryId: string;
}

/** React Query key of a single journal entry (with its attachments). */
export function journalEntryQueryKey(aquariumId: string, entryId: string) {
  return ['journalEntry', aquariumId, entryId] as const;
}

export function attachedImageSrc(image: AttachedImage): string | undefined {
  return image.thumbnailUrl ?? image.displayUrl ?? image.originalUrl ?? undefined;
}

/**
 * The photos attached to a saved journal entry: thumbnails with a remove
 * action, plus the AddPhotoControl to attach more. Removing only unlinks the
 * photo — the image stays in the aquarium gallery.
 */
export function JournalEntryPhotos({ aquariumId, entryId }: JournalEntryPhotosProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = journalEntryQueryKey(aquariumId, entryId);
  const [message, setMessage] = useState<{ key: string; isError: boolean } | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const { data: entry, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => getJournalEntry(aquariumId, entryId),
    enabled: Boolean(aquariumId && entryId),
  });

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: journalEntryQueryKey(aquariumId, entryId) });
  }, [aquariumId, entryId, queryClient]);

  const handleRemove = useCallback(
    async (imageId: string) => {
      setRemovingId(imageId);
      try {
        await unlinkPhotoFromEntry(aquariumId, entryId, imageId);
        setMessage({ key: 'journal.attachments.removed', isError: false });
        refresh();
      } catch {
        setMessage({ key: 'journal.attachments.unlinkError', isError: true });
      } finally {
        setRemovingId(null);
      }
    },
    [aquariumId, entryId, refresh],
  );

  const images = entry?.attachedImages ?? [];
  const title = entry?.title ?? '';

  let list;
  if (isLoading) {
    list = <Text color="muted">{t('journal.attachments.loading')}</Text>;
  } else if (isError) {
    list = (
      <Alert compact>{t('journal.attachments.loadError')}</Alert>
    );
  } else if (images.length === 0) {
    list = (
      <Text color="muted" data-testid="journal-entry-photos-empty">
        {t('journal.attachments.empty')}
      </Text>
    );
  } else {
    list = (
      <ul className="journal-entry-photos__list" aria-label={t('journal.attachments.title')}>
        {images.map((image, index) => (
          <li key={image.id}>
            <Card padding="sm" className="journal-entry-photos__item">
              <img
                src={attachedImageSrc(image)}
                alt={t('journal.attachments.photoAlt', { number: index + 1, title })}
                className="journal-entry-photos__image"
                loading="lazy"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                isLoading={removingId === image.id}
                disabled={removingId !== null}
                aria-label={t('journal.attachments.removeLabel', { number: index + 1 })}
                onClick={() => void handleRemove(image.id)}
              >
                {t('journal.attachments.remove')}
              </Button>
            </Card>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="journal-entry-photos" data-testid="journal-entry-photos">
      {list}
      {message?.isError && <Alert compact>{t(message.key)}</Alert>}
      <LiveRegion className="journal-entry-photos__status">
        {message && !message.isError && (
          <Text size="sm" color="secondary">
            {t(message.key)}
          </Text>
        )}
      </LiveRegion>
      <AddPhotoControl
        aquariumId={aquariumId}
        entryId={entryId}
        attachedImageIds={images.map((image) => image.id)}
        onAttached={refresh}
      />
    </div>
  );
}
